import {
  ComponentPropsWithoutRef,
  MouseEventHandler,
  Ref,
  forwardRef,
  useEffect,
  useRef,
} from "react";
import { AutoCompleteSingleInputProps } from "./props";
import { useAutoComplete } from "./common";
import { useSignalValue } from "@/utils/signal";
import { PopoverAnchor } from "@/kit/ui/popover";
import { Button } from "@/kit/ui/button";
import { XCloseIcon } from "@/icons";
import { inputInputVariants } from "@/kit/helpers/input";
import { cn } from "@/utils/tailwind";
import { LoadingAnchor } from "./auto-complete";
import { setRefs } from "@/utils/react";
import { ActualInput, ToggleButton } from "./input";

export const AutoCompleteSingleInput = forwardRef(
  function AutoCompleteSingleInput(
    { className, complete, children, ...props }: AutoCompleteSingleInputProps,
    ref: Ref<HTMLInputElement>,
  ) {
    const { disabled, "aria-invalid": invalid } = props;

    const inputRef = useRef<HTMLInputElement>(null);
    const { open: open_ } = useAutoComplete();
    const isOpen = useSignalValue(open_);

    useEffect(() => {
      if (isOpen) {
        inputRef.current?.focus();
      } else {
        inputRef.current?.blur();
      }
    }, [isOpen]);

    return (
      <PopoverAnchor>
        <LoadingAnchor
          className={inputInputVariants({
            focused: isOpen,
            disabled,
            invalid: invalid === "true" || invalid === true,
            className: cn("flex gap-2 min-w-72", className),
          })}
        >
          {children}

          {complete ? (
            <div className="flex grow justify-end">
              <ClearButton />
            </div>
          ) : (
            <div className="flex flex-1">
              <ActualInput ref={setRefs(ref, inputRef)} {...props} />
              <ToggleButton />
            </div>
          )}
        </LoadingAnchor>
      </PopoverAnchor>
    );
  },
);

export function ClearButton({
  onClick: onClick_,
  ...buttonProps
}: ComponentPropsWithoutRef<"button">) {
  const { onPopItem } = useAutoComplete();

  const onClick: MouseEventHandler<HTMLButtonElement> = (evt) => {
    onClick_?.(evt);
    if (!evt.defaultPrevented) {
      onPopItem();
    }
    evt.preventDefault();
  };
  return (
    <Button {...buttonProps} variant="tertiary" size="xs" onClick={onClick}>
      <XCloseIcon className="w-4 h-4" />
    </Button>
  );
}
